import { ErrorHandler, Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { LoggingService } from './logging.service';
import { NotificationService } from './notification.service';

@Injectable({ providedIn: 'root' })
export class GlobalErrorHandler implements ErrorHandler {
    readonly #router = inject(Router);
    readonly #loggingService = inject(LoggingService);
    readonly #notificationService = inject(NotificationService);

    handleError(error: unknown): void {
        this.#loggingService.logError(error);

        if (error instanceof HttpErrorResponse) {
            this.handleHttpError(error);
        } else {
            this.handleGenericError(error);
        }
    }

    private handleHttpError(error: HttpErrorResponse): void {
        switch (error.status) {
            case 0:
                this.#notificationService.showError(
                    'Network Error',
                    'Could not connect to the server. Please check your internet connection.',
                );
                break;
            case 400:
                this.#notificationService.showError(
                    'Invalid Request',
                    'The request could not be processed. Please try again.',
                );
                break;
            case 401:
                break;
            case 403:
                this.#notificationService.showError(
                    'Access Denied',
                    'You do not have permission to access this resource.',
                );
                break;
            case 404:
                this.#notificationService.showError('Not Found', 'The requested resource could not be found.');
                break;
            case 500:
            case 502:
            case 503:
            case 504:
                this.#notificationService.showError(
                    'Server Error',
                    'Something went wrong on our servers. Please try again later.',
                );
                break;
            default:
                this.#notificationService.showError('Error', 'An unexpected error occurred. Please try again.');
        }
    }

    private handleGenericError(error: unknown): void {
        const errorMessage = error instanceof Error ? error.message : String(error);

        this.#notificationService.showError(
            'Application Error',
            'An unexpected error occurred. Our team has been notified.',
        );

        // In a production app, we might want to navigate to an error page for critical errors
        // this.#router.navigate(['/error']);
    }
}
